import { useState } from 'react';
import { Bike, Loader2, Plus, RefreshCw, ShoppingBag, Users } from 'lucide-react';

import TableShapeIcon from '../../tables/components/TableShapeIcon';
import CompactSelectField from '../../../../components/Forms/Input/CompactSelectField';
import ToggleButtonGroup from '../../../../components/Forms/Buttons/ToggleButtonGroup';

import { RestaurantDiningRoomDTO } from '../../../../types/restaurant/RestaurantDiningRoomTypes';
import { RestaurantTableDTO } from '../../../../types/restaurant/RestaurantTableTypes';
import {
  RestaurantOrderDTO,
  RestaurantOrderServiceType,
} from '../../../../types/restaurant/RestaurantOrderTypes';
import {
  formatOrderMoney,
  orderCardStatus,
  orderLabels,
  serviceTypeLabels,
  tableStatus,
} from '../orderPresentation';

interface OrderTablesPanelProps {
  rooms: RestaurantDiningRoomDTO[];
  tables: RestaurantTableDTO[];
  selectedRoomId: number | 'ALL';
  selectedTable?: RestaurantTableDTO | null;
  loading: boolean;
  disabled: boolean;
  onRefresh: () => void;
  onRoomChange: (roomId: number | 'ALL') => void;
  onSelectTable: (table: RestaurantTableDTO) => void;
  serviceType: RestaurantOrderServiceType;
  onServiceTypeChange: (type: RestaurantOrderServiceType) => void;
  tablelessOrders: RestaurantOrderDTO[];
  tablelessLoading: boolean;
  selectedOrderId?: number | null;
  onSelectTablelessOrder: (orderId: number) => void;
  onStartTablelessOrder: () => void;
}

const OrderTablesPanel = ({
  rooms,
  tables,
  selectedRoomId,
  selectedTable,
  loading,
  disabled,
  onRefresh,
  onRoomChange,
  onSelectTable,
  serviceType,
  onServiceTypeChange,
  tablelessOrders,
  tablelessLoading,
  selectedOrderId,
  onSelectTablelessOrder,
  onStartTablelessOrder,
}: OrderTablesPanelProps) => {
  const [activeStatusKey, setActiveStatusKey] = useState<
    keyof typeof tableStatus | null
  >(null);
  const isDineIn = serviceType === 'DINE_IN';

  return (
    <section className="flex min-h-[640px] flex-col bg-white dark:bg-boxdark">
      <div className="border-b border-stroke bg-gray-2/50 p-4 dark:border-strokedark dark:bg-meta-4/20">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-black dark:text-white">
              Salones y mesas
            </h2>

            <p className="text-xs text-bodydark2">
              {isDineIn
                ? 'Seleccione una mesa libre u ocupada.'
                : `Órdenes activas · ${serviceTypeLabels[serviceType]}`}
            </p>
          </div>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading || disabled}
            className="rounded-xl border border-stroke p-2 text-bodydark2 transition hover:border-primary hover:bg-primary/5 hover:text-primary disabled:opacity-40 dark:border-strokedark"
            title="Actualizar"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <ToggleButtonGroup
          className="mb-3"
          fullWidth
          value={serviceType}
          disabled={disabled}
          onChange={onServiceTypeChange}
          options={[
            { value: 'DINE_IN', label: 'Mesa' },
            { value: 'TAKEAWAY', label: 'Para llevar' },
            { value: 'DELIVERY', label: 'Domicilio' },
          ]}
        />

        {isDineIn && rooms.length > 4 ? (
          <CompactSelectField
            name="ordersRoomFilter"
            value={selectedRoomId}
            iconKey="store"
            options={[
              { value: 'ALL', label: 'Todos los salones' },
              ...rooms.map((room) => ({ value: room.id, label: room.name })),
            ]}
            onChange={(event) =>
              onRoomChange(
                event.target.value === 'ALL'
                  ? 'ALL'
                  : Number(event.target.value)
              )
            }
          />
        ) : isDineIn ? (
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => onRoomChange('ALL')}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                selectedRoomId === 'ALL'
                  ? 'bg-primary text-white'
                  : 'bg-gray-2 hover:bg-gray-3 dark:bg-meta-4'
              }`}
            >
              Todos
            </button>

            {rooms.map((room) => (
              <button
                type="button"
                key={room.id}
                onClick={() => onRoomChange(room.id)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  selectedRoomId === room.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-2 hover:bg-gray-3 dark:bg-meta-4'
                }`}
              >
                {room.name}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {!isDineIn && (
        <div className="flex-1 overflow-y-auto p-4">
          <button
            type="button"
            onClick={onStartTablelessOrder}
            className={`mb-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed py-4 text-sm font-semibold transition ${
              selectedOrderId == null
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-stroke text-bodydark2 hover:border-primary hover:text-primary dark:border-strokedark'
            }`}
          >
            <Plus className="h-4 w-4" />
            Nueva orden · {serviceTypeLabels[serviceType]}
          </button>

          {tablelessLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : tablelessOrders.length === 0 ? (
            <p className="py-10 text-center text-sm text-bodydark2">
              No hay órdenes activas de este tipo.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {tablelessOrders.map((tablelessOrder) => {
                const selected = selectedOrderId === tablelessOrder.id;
                const cardStatus = orderCardStatus[tablelessOrder.status];
                const ServiceIcon = serviceType === 'DELIVERY' ? Bike : ShoppingBag;

                return (
                  <button
                    type="button"
                    key={tablelessOrder.id}
                    onClick={() => onSelectTablelessOrder(tablelessOrder.id)}
                    className={`flex w-full items-center gap-3 overflow-hidden rounded-2xl border p-3 text-left transition ${
                      cardStatus.classes
                    } ${
                      selected
                        ? 'ring-2 ring-primary ring-offset-2 dark:ring-offset-boxdark'
                        : ''
                    }`}
                  >
                    <span
                      className={`relative flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${cardStatus.surface}`}
                    >
                      <ServiceIcon className={`h-7 w-7 ${cardStatus.icon}`} />
                      <span
                        className={`absolute right-1 top-1 h-2.5 w-2.5 rounded-full ${cardStatus.dot}`}
                      />
                    </span>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate font-semibold text-black dark:text-white">
                          {tablelessOrder.orderNumber ?? `#${tablelessOrder.id}`}
                        </p>
                        <span className="shrink-0 text-sm font-semibold text-primary">
                          {formatOrderMoney(tablelessOrder.total)}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-xs text-bodydark2">
                        {tablelessOrder.customerName ?? 'Sin cliente'}
                      </p>
                      <span
                        className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cardStatus.chip}`}
                      >
                        {orderLabels[tablelessOrder.status]}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className={`flex w-full ${isDineIn ? '' : 'hidden'}`}>
        {Object.entries(tableStatus).map(([key, value]) => (
          <button
            key={key}
            type="button"
            title={value.label}
            aria-label={value.label}
            onClick={() =>
              setActiveStatusKey((prev) =>
                prev === key ? null : (key as keyof typeof tableStatus)
              )
            }
            className={`h-3 flex-1 ${value.dot} transition-opacity hover:opacity-90 ${
              activeStatusKey && activeStatusKey !== key ? 'opacity-40' : ''
            }`}
          />
        ))}
      </div>

      {isDineIn && activeStatusKey && (
        <div className="border-b border-stroke bg-gray-2/60 py-1.5 text-center text-xs font-semibold text-black dark:border-strokedark dark:bg-meta-4/20 dark:text-white">
          {tableStatus[activeStatusKey].label}
        </div>
      )}

      {isDineIn && (loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
          {tables.map((table) => {
            const status = tableStatus[table.status];
            const selected = selectedTable?.id === table.id;

            return (
              <button
                type="button"
                key={table.id}
                disabled={!['FREE', 'OCCUPIED'].includes(table.status)}
                onClick={() => onSelectTable(table)}
                className={`flex flex-col overflow-hidden rounded-2xl border text-left transition ${
                  status.classes
                } ${
                  selected
                    ? 'ring-2 ring-primary ring-offset-2 dark:ring-offset-boxdark'
                    : ''
                } disabled:cursor-not-allowed`}
              >
                <div
                  className={`relative flex items-center justify-center py-5 ${status.surface}`}
                >
                  <TableShapeIcon
                    shape={table.shape}
                    capacity={table.capacity}
                    className={`h-16 w-16 ${status.icon}`}
                  />
                  <span
                    className={`absolute right-2.5 top-2.5 h-2.5 w-2.5 rounded-full ${status.dot}`}
                  />
                </div>

                <div className="border-t border-black/5 bg-white/60 p-3 dark:border-white/10 dark:bg-boxdark/50">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate font-semibold text-black dark:text-white">
                      {table.name}
                    </p>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-bodydark2">
                      <Users className="h-3.5 w-3.5" />
                      {table.capacity}
                    </span>
                  </div>

                  <span
                    className={`mt-1.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${status.chip}`}
                  >
                    {status.label}
                  </span>
                </div>
              </button>
            );
          })}

          {tables.length === 0 && (
            <p className="col-span-full py-16 text-center text-sm text-bodydark2">
              No hay mesas activas en este salón.
            </p>
          )}
        </div>
      ))}

    </section>
  );
};

export default OrderTablesPanel;
